import { asArray, asBool, asNumber, asRecord, asString, assertArray } from "@/data/mapper/common/mapper-utils";
import {
  ActivityChoice,
  ActivityDataSet,
  ActivityDetail,
  ActivityEventData,
  ActivityFeedback,
  ActivityPerformance,
  BreathingData,
  CloudRecordData,
  CloudDetailData,
  CognitiveRestructuring,
  DiscomfortInfo,
  EyeActivityData,
  EyeActivityDataModel,
  EyeActivitySession,
  HeartRateInfo,
  HeartRatePoint,
  Plan,
  PrescriptionDetail,
  PrescriptionSummary,
  RatedActivityChoice,
  RatedActivityDetailData,
  RatedActivityRecordData,
  WalkRecordData,
  WalkDetailData,
  WeeklyPerformance,
} from "@/domain/entity/prescription/prescription-info-entity";

export class PrescriptionInfoMapper {
  static toSummaryEntity(dto: unknown): PrescriptionSummary {
    const map = asRecord(dto);
    const doctor = asRecord(map.doctor);
    const doctorName =
      map.doctorName == null && map.medicalExpertName == null && doctor.name == null
        ? null
        : asString(map.doctorName ?? map.medicalExpertName ?? doctor.name);
    return {
      prescriptionUuid: asString(map.prescriptionUuid ?? map.uuid ?? map.treatmentUuid),
      prescriptionId:
        map.prescriptionId == null && map.treatmentId == null && map.step == null
          ? null
          : asString(map.prescriptionId ?? map.treatmentId ?? map.step),
      prescriptionStatus:
        map.prescriptionStatus == null &&
        map.status == null &&
        map.state == null &&
        map.progressStatus == null &&
        map.treatmentStatus == null &&
        map.treatmentState == null
          ? null
          : asString(
              map.prescriptionStatus ??
                map.status ??
                map.state ??
                map.progressStatus ??
                map.treatmentStatus ??
                map.treatmentState,
            ),
      prescriptionType:
        map.prescriptionType == null && map.type == null
          ? null
          : asString(map.prescriptionType ?? map.type),
      startDate:
        map.startDate == null && map.prescriptionStartDate == null
          ? null
          : asString(map.startDate ?? map.prescriptionStartDate),
      endDate:
        map.endDate == null && map.prescriptionEndDate == null
          ? null
          : asString(map.endDate ?? map.prescriptionEndDate),
      complianceRate: map.complianceRate == null ? null : asNumber(map.complianceRate),
      ...(doctorName == null ? {} : { doctorName }),
    };
  }

  static toSummaryEntityList(dto: unknown): PrescriptionSummary[] {
    return assertArray(dto, "PrescriptionInfoMapper::toSummaryEntityList").map((item) =>
      PrescriptionInfoMapper.toSummaryEntity(item),
    );
  }

  static toDetailEntity(
    dto: unknown,
    patientUuid: string,
    prescriptionUuid: string,
    activityList?: unknown,
  ): PrescriptionDetail {
    const map = asRecord(dto);
    const compliance = asRecord(map.compliance);
    const doctor = asRecord(map.doctor);
    const rawPlans = activityList ?? map.plans ?? map.planList ?? map.activities ?? map.activityList ?? [];
    const rawActivities = map.activities ?? map.activityList ?? [];
    const rawWeeklyPerformance = asArray(map.weeklyPerformance).length
      ? map.weeklyPerformance
      : compliance.weekly;
    const plans = asArray(rawPlans)
      .map((item) => PrescriptionInfoMapper.toPlanEntity(item))
      .sort(comparePlanByRecentDate);
    return {
      ...PrescriptionInfoMapper.toSummaryEntity({
        ...map,
        prescriptionUuid,
        complianceRate: map.complianceRate ?? compliance.complianceRate,
      }),
      patientUuid,
      plans,
      activities: Array.isArray(rawActivities) ? rawActivities.map((item) => asRecord(item)) : [],
      memo: map.memo == null ? null : asString(map.memo),
      timeZone: map.timeZone == null ? null : asString(map.timeZone),
      doctorId:
        map.doctorId == null && doctor.screeningId == null
          ? null
          : asString(map.doctorId ?? doctor.screeningId),
      doctorName:
        map.doctorName == null && doctor.name == null
          ? null
          : asString(map.doctorName ?? doctor.name),
      progressRate:
        map.progressRate == null && compliance.progressRate == null
          ? null
          : asNumber(map.progressRate ?? compliance.progressRate),
      performanceRate:
        map.performanceRate == null && compliance.performanceRate == null
          ? null
          : asNumber(map.performanceRate ?? compliance.performanceRate),
      weeklyPerformance: asArray(rawWeeklyPerformance).map((item) =>
        PrescriptionInfoMapper.toWeeklyPerformanceEntity(item),
      ),
    };
  }

  static toActivityDetailEntity(dto: unknown): ActivityDetail {
    const map = asRecord(dto);
    const startAt = map.startAt == null ? null : asString(map.startAt);
    const endAt = map.endAt == null ? null : asString(map.endAt);
    const eyeActivityData = asArray(map.eyeActivityData).map((item) =>
      PrescriptionInfoMapper.toEyeActivitySessionEntity(item),
    );
    const breathingList = asArray(map.breathingList).length
      ? asArray(map.breathingList).map((item) => PrescriptionInfoMapper.toBreathingDataEntity(item))
      : breathingListFromEyeActivityData(eyeActivityData);
    const cloudData = asArray(map.cloudData).map((item) =>
      PrescriptionInfoMapper.toCloudRecordDataEntity(item),
    );
    const coreData = asArray(map.coreData).map((item) =>
      PrescriptionInfoMapper.toRatedActivityRecordDataEntity(item),
    );
    const energyData = asArray(map.energyData).map((item) =>
      PrescriptionInfoMapper.toRatedActivityRecordDataEntity(item),
    );
    const walkData = asArray(map.walkData).map((item) =>
      PrescriptionInfoMapper.toWalkRecordDataEntity(item),
    );
    return {
      name: map.name == null ? null : asString(map.name),
      startAt,
      endAt,
      breathingList,
      heartRateInfo: PrescriptionInfoMapper.toHeartRateInfoEntity(
        map.heartRateInfo ?? { hrData: map.heartRateData },
        startAt,
      ),
      activityEventData: asArray(map.activityEventData).map((item) =>
        PrescriptionInfoMapper.toActivityEventDataEntity(item),
      ),
      discomfortInfo: activityDiscomfortInfo(map),
      cloudInfo: asArray(map.cloudInfo).map((item) =>
        PrescriptionInfoMapper.toActivityDataSetEntity(
          item,
          PrescriptionInfoMapper.toCloudDetailDataEntity,
        ),
      ),
      cloudData,
      coreInfo: activityDataSetsFromRecords(
        map.coreInfo,
        coreData,
        PrescriptionInfoMapper.toRatedActivityDetailDataEntity,
      ),
      coreData,
      energyInfo: activityDataSetsFromRecords(
        map.energyInfo,
        energyData,
        PrescriptionInfoMapper.toRatedActivityDetailDataEntity,
      ),
      energyData,
      walkInfo: asArray(map.walkInfo).length
        ? asArray(map.walkInfo).map((item) =>
            PrescriptionInfoMapper.toActivityDataSetEntity(
              item,
              PrescriptionInfoMapper.toWalkDetailDataEntity,
            ),
          )
        : walkData.map((item, index) => ({
            index,
            detailData: { result: item.step },
          })),
      walkData,
      eyeActivityData,
      feedbacks: PrescriptionInfoMapper.toActivityFeedbacks(map),
    };
  }

  private static toActivityFeedbacks(map: Record<string, unknown>): ActivityFeedback[] {
    const feedbacks = asArray(map.feedbacks)
      .map((item): ActivityFeedback | null => {
        const feedback = asRecord(item);
        const comment =
          feedback.comment == null && feedback.feedback == null
            ? ""
            : asString(feedback.comment ?? feedback.feedback).trim();
        if (!comment) return null;
        return {
          comment,
          createdAt: feedback.createdAt == null ? null : asString(feedback.createdAt),
        };
      })
      .filter((item): item is ActivityFeedback => item != null);

    if (feedbacks.length > 0) return feedbacks;

    const legacyFeedback = map.feedback == null ? "" : asString(map.feedback).trim();
    if (!legacyFeedback) return [];
    return [{ comment: legacyFeedback, createdAt: null }];
  }

  static toEyeActivityDataModelEntity(dto: unknown): EyeActivityDataModel {
    const map = asRecord(dto);
    return {
      result1: asArray(map.result1).map((item) =>
        PrescriptionInfoMapper.toEyeActivityDataEntity(item),
      ),
      result2: asArray(map.result2).map((item) =>
        PrescriptionInfoMapper.toEyeActivityDataEntity(item),
      ),
      result3: asArray(map.result3).map((item) =>
        PrescriptionInfoMapper.toEyeActivityDataEntity(item),
      ),
      result4: asArray(map.result4).map((item) =>
        PrescriptionInfoMapper.toEyeActivityDataEntity(item),
      ),
    };
  }

  private static toWeeklyPerformanceEntity(dto: unknown): WeeklyPerformance {
    const map = asRecord(dto);
    const rawDays = asArray(map.days);
    const rawActivities = rawDays.length ? rawDays : asArray(map.activities);
    const days = rawActivities.map((day, index) =>
      PrescriptionInfoMapper.toWeeklyActivityPerformanceEntity(day, index),
    );
    return {
      week: asNumber(map.week),
      compliance:
        map.compliance == null && map.complianceRate == null
          ? weeklyComplianceFromActivities(days)
          : asNumber(map.compliance ?? map.complianceRate),
      days,
    };
  }

  private static toWeeklyActivityPerformanceEntity(
    dto: unknown,
    index: number,
  ): ActivityPerformance {
    const map = asRecord(dto);
    const name = asString(map.name ?? map.activityName ?? map.title);
    return {
      day:
        map.day == null && map.progressDay == null
          ? index + 1
          : asNumber(map.day ?? map.progressDay),
      type:
        map.type == null && map.activityType == null
          ? regularActivityTypeForName(name)
          : asString(map.type ?? map.activityType),
      name,
      progress: rateNumberFromUnknown(
        map.progress ??
          map.progressRate ??
          map.complianceRate ??
          map.performanceRate ??
          map.rate,
      ),
      totalTime: asNumber(map.totalTime ?? map.totalTimeSeconds ?? map.durationSeconds ?? map.duration),
    };
  }

  private static toPlanEntity(dto: unknown): Plan {
    const map = asRecord(dto);
    const isCompleted = map.isCompleted ?? map.status;
    const rawStatus =
      typeof isCompleted === "boolean" ? (isCompleted ? "완료" : "미완료") : isCompleted;
    return {
      planUuid: asString(map.planUuid ?? map.uuid ?? map.activityUuid),
      title: asString(map.title ?? map.activityName ?? map.name, ""),
      day:
        map.day == null && map.aFewDays == null && map.progressDay == null
          ? undefined
          : asNumber(map.day ?? map.aFewDays ?? map.progressDay),
      status: rawStatus == null ? undefined : asString(rawStatus),
      activityCount: map.activityCount == null ? undefined : asNumber(map.activityCount),
      type: map.type == null ? null : asString(map.type),
      name: map.name == null ? null : asString(map.name),
      startAt: map.startAt == null && map.startTime == null
        ? null
        : activityDateTime(map.date, map.startAt ?? map.startTime),
      endAt: map.endAt == null && map.endTime == null
        ? null
        : activityDateTime(map.date, map.endAt ?? map.endTime),
      totalTime:
        map.totalTime == null && map.totalTimeSeconds == null && map.durationSeconds == null && map.duration == null
          ? null
          : asNumber(map.totalTime ?? map.totalTimeSeconds ?? map.durationSeconds ?? map.duration),
    };
  }

  private static toHeartRateInfoEntity(dto: unknown, activityStartAt?: string | null): HeartRateInfo {
    const map = asRecord(dto);
    const activityStartTime = dateTimeValue(activityStartAt);
    const hrData = asArray(map.hrData).map((item) =>
      PrescriptionInfoMapper.toHeartRatePointEntity(item, activityStartTime),
    );
    const values = hrData.map((item) => item.heartRate);
    const highest = values.length ? Math.max(...values) : 0;
    const lowest = values.length ? Math.min(...values) : 0;
    const average = values.length
      ? values.reduce((sum, value) => sum + value, 0) / values.length
      : 0;

    return {
      highestHeartRate: asNumber(map.highestHeartRate, highest),
      lowestHeartRate: asNumber(map.lowestHeartRate, lowest),
      averageHeartRate: asNumber(map.averageHeartRate, average),
      hrData,
    };
  }

  private static toHeartRatePointEntity(dto: unknown, activityStartTime?: number): HeartRatePoint {
    const map = asRecord(dto);
    return {
      heartRate: asNumber(map.heartRate ?? map.hr),
      time: heartRateActivityTimeValue(map.time, activityStartTime),
    };
  }

  private static toBreathingDataEntity(dto: unknown): BreathingData {
    const map = asRecord(dto);
    return {
      startTime: asString(map.startTime ?? map.startAt),
      endTime: map.endTime == null && map.endAt == null ? null : asString(map.endTime ?? map.endAt),
    };
  }

  static toDiscomfortInfoEntity(dto: unknown): DiscomfortInfo {
    const map = asRecord(dto);
    const isAfter = map.isAfter === true;
    return {
      id: map.id == null ? undefined : asNumber(map.id),
      activityId: map.activityId == null ? undefined : asNumber(map.activityId),
      when: map.when == null ? (isAfter ? "after" : "before") : asString(map.when),
      score: asNumber(map.score),
    };
  }

  private static toActivityEventDataEntity(dto: unknown): ActivityEventData {
    const map = asRecord(dto);
    return {
      eventAt: asString(map.eventAt ?? map.time ?? map.at),
      type: asString(map.type),
    };
  }

  private static toCloudRecordDataEntity(dto: unknown): CloudRecordData {
    const map = asRecord(dto);
    return {
      time: map.time == null ? null : asString(map.time),
      q: map.q == null ? null : asString(map.q),
      a: map.a == null ? null : asString(map.a),
    };
  }

  private static toWalkRecordDataEntity(dto: unknown): WalkRecordData {
    const map = asRecord(dto);
    return {
      time: map.time == null ? null : asString(map.time),
      step: asNumber(map.step ?? map.result),
    };
  }

  private static toRatedActivityRecordDataEntity(dto: unknown): RatedActivityRecordData {
    const map = asRecord(dto);
    return {
      choices: asArray(map.choices).map((item) =>
        PrescriptionInfoMapper.toRatedActivityChoiceEntity(item),
      ),
      selected: map.selected == null ? null : asString(map.selected),
    };
  }

  static toActivityDataSetEntity<T>(
    dto: unknown,
    detailMapper: (value: unknown) => T,
  ): ActivityDataSet<T> {
    const map = asRecord(dto);
    return {
      index: asNumber(map.index),
      detailData: detailMapper(map.detailData),
    };
  }

  private static toCloudDetailDataEntity(dto: unknown): CloudDetailData {
    const map = asRecord(dto);
    return {
      choices: asArray(map.choices).map((item) =>
        PrescriptionInfoMapper.toActivityChoiceEntity(item),
      ),
      cognitiveRestructuring: asArray(map.cognitiveRestructuring).map((item) =>
        PrescriptionInfoMapper.toCognitiveRestructuringEntity(item),
      ),
    };
  }

  private static toRatedActivityDetailDataEntity(dto: unknown): RatedActivityDetailData {
    const map = asRecord(dto);
    return {
      choices: asArray(map.choices).map((item) =>
        PrescriptionInfoMapper.toRatedActivityChoiceEntity(item),
      ),
      selectedChoices: asArray(map.selectedChoices).map((item) =>
        PrescriptionInfoMapper.toRatedActivityChoiceEntity(item),
      ),
    };
  }

  private static toWalkDetailDataEntity(dto: unknown): WalkDetailData {
    const map = asRecord(dto);
    return {
      result: asNumber(map.result),
    };
  }

  private static toActivityChoiceEntity(dto: unknown): ActivityChoice {
    const map = asRecord(dto);
    return {
      id: map.id == null ? null : asString(map.id),
      title: map.title == null ? null : asString(map.title),
      childrenChoices: asArray(map.childrenChoices).map((item) =>
        PrescriptionInfoMapper.toActivityChoiceEntity(item),
      ),
    };
  }

  private static toRatedActivityChoiceEntity(dto: unknown): RatedActivityChoice {
    const map = asRecord(dto);
    return {
      title: asString(map.title, "-"),
      isUse: map.isUse == null ? undefined : asBool(map.isUse),
      rate: map.rate == null ? null : asNumber(map.rate),
    };
  }

  private static toCognitiveRestructuringEntity(dto: unknown): CognitiveRestructuring {
    const map = asRecord(dto);
    return {
      cognitiveThinkingId: asString(map.cognitiveThinkingId),
      restructuredThinkingId: asString(map.restructuredThinkingId),
    };
  }

  private static toEyeActivityDataEntity(dto: unknown): EyeActivityData {
    const map = asRecord(dto);
    return {
      time: asString(map.time),
      x: asNumber(map.x),
      y: asNumber(map.y),
    };
  }

  private static toEyeActivitySessionEntity(dto: unknown): EyeActivitySession {
    const map = asRecord(dto);
    const deepBreath = asRecord(map.deepBreath);
    const eyeActivity = asRecord(map.eyeActivity);
    return {
      deepBreath:
        Object.keys(deepBreath).length === 0
          ? null
          : {
              startedAt: deepBreath.startedAt == null ? null : asString(deepBreath.startedAt),
              endedAt: deepBreath.endedAt == null ? null : asString(deepBreath.endedAt),
            },
      eyeActivity:
        Object.keys(eyeActivity).length === 0
          ? null
          : {
              startedAt: eyeActivity.startedAt == null ? null : asString(eyeActivity.startedAt),
              endedAt: eyeActivity.endedAt == null ? null : asString(eyeActivity.endedAt),
              eyeMovementType:
                eyeActivity.eyeMovementType == null ? null : asString(eyeActivity.eyeMovementType),
              eyePosition: asArray(eyeActivity.eyePosition).map((item) =>
                PrescriptionInfoMapper.toEyeActivityDataEntity(item),
              ),
            },
    };
  }
}

function activityDateTime(date: unknown, time: unknown): string {
  const value = asString(time);
  if (!value) return value;
  if (!date || value.includes("T")) return value;
  return `${asString(date)}T${value}`;
}

function heartRateActivityTimeValue(value: unknown, activityStartTime?: number): string {
  const rawValue = asString(value);
  if (!rawValue) return rawValue;

  const activitySeconds = activitySecondsFromTimeValue(rawValue);
  if (activitySeconds !== null) return String(activitySeconds);

  const actualTime = dateTimeValue(rawValue);
  const startTime = Number.isFinite(activityStartTime) ? activityStartTime : null;
  if (!Number.isFinite(actualTime) || startTime == null) return rawValue;

  return String(Math.max(0, Math.round((actualTime - startTime) / 1000)));
}

function activitySecondsFromTimeValue(value: string): number | null {
  const normalized = value.trim();
  if (!normalized) return null;
  if (/^\d+(\.\d+)?$/.test(normalized)) return Math.max(0, Math.round(Number(normalized)));

  const parts = normalized.split(":");
  if (parts.length < 2 || parts.length > 3) return null;

  const numbers = parts.map((part) => Number(part));
  if (numbers.some((part) => !Number.isFinite(part) || part < 0)) return null;

  if (numbers.length === 2) {
    const [minutes, seconds] = numbers;
    return Math.round(minutes * 60 + seconds);
  }

  const [hours, minutes, seconds] = numbers;
  return Math.round(hours * 3600 + minutes * 60 + seconds);
}

function dateTimeValue(value?: string | null): number {
  return new Date(value ?? "").getTime();
}

function regularActivityTypeForName(name: string): string {
  switch (name) {
    case "walk":
    case "nature":
    case "eyeActivity":
      return "external";
    default:
      return "internal";
  }
}

function rateNumberFromUnknown(value: unknown): number {
  const direct = finiteNumber(value);
  if (direct !== null) return direct;

  const map = asRecord(value);
  const directKeys = [
    "progress",
    "progressRate",
    "complianceRate",
    "performanceRate",
    "rate",
    "percentage",
    "percent",
    "value",
  ];
  for (const key of directKeys) {
    const numeric = finiteNumber(map[key]);
    if (numeric !== null) return numeric;
  }

  const numerator = firstFiniteNumber(map, [
    "completed",
    "completedCount",
    "completeCount",
    "done",
    "doneCount",
    "current",
    "count",
    "performed",
    "performedCount",
    "actual",
  ]);
  const denominator = firstFiniteNumber(map, [
    "total",
    "totalCount",
    "target",
    "targetCount",
    "required",
    "requiredCount",
    "goal",
    "expected",
  ]);
  if (numerator !== null && denominator !== null && denominator > 0) {
    return numerator / denominator;
  }

  return 0;
}

function finiteNumber(value: unknown): number | null {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function firstFiniteNumber(map: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const numeric = finiteNumber(map[key]);
    if (numeric !== null) return numeric;
  }
  return null;
}

function weeklyComplianceFromActivities(days: ActivityPerformance[]): number {
  if (days.length === 0) return 0;
  return days.reduce((sum, day) => sum + rateNumberFromUnknown(day.progress), 0) / days.length;
}

function comparePlanByRecentDate(left: Plan, right: Plan): number {
  const dateDiff = planDateSortValue(right) - planDateSortValue(left);
  if (dateDiff !== 0) return dateDiff;
  return (right.day ?? 0) - (left.day ?? 0);
}

function planDateSortValue(plan: Plan): number {
  const value = plan.startAt ?? plan.endAt;
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function activityDiscomfortInfo(map: Record<string, unknown>): DiscomfortInfo[] {
  const discomfortInfo = asArray(map.discomfortInfo);
  if (discomfortInfo.length) {
    return discomfortInfo.map((item) => PrescriptionInfoMapper.toDiscomfortInfoEntity(item));
  }

  const discomfort = asRecord(map.discomfort);
  const records: DiscomfortInfo[] = [];
  if (discomfort.before != null) {
    records.push({
      when: "before",
      score: asNumber(discomfort.before),
    });
  }
  if (discomfort.after != null) {
    records.push({
      when: "after",
      score: asNumber(discomfort.after),
    });
  }
  return records;
}

function breathingListFromEyeActivityData(eyeActivityData: EyeActivitySession[]): BreathingData[] {
  return eyeActivityData
    .map((item) => item.deepBreath)
    .filter((item): item is NonNullable<EyeActivitySession["deepBreath"]> =>
      Boolean(item?.startedAt),
    )
    .map((item) => ({
      startTime: asString(item.startedAt),
      endTime: item.endedAt == null ? null : asString(item.endedAt),
    }));
}

function activityDataSetsFromRecords<T>(
  legacyValue: unknown,
  records: RatedActivityRecordData[],
  detailMapper: (value: unknown) => T,
): ActivityDataSet<T>[] {
  const legacy = asArray(legacyValue);
  if (legacy.length) {
    return legacy.map((item) =>
      PrescriptionInfoMapper.toActivityDataSetEntity(item, detailMapper),
    );
  }

  return records.map((record, index) => ({
    index,
    detailData: detailMapper({
      choices: record.choices,
      selectedChoices: record.selected
        ? record.choices.filter((choice) => choice.title === record.selected)
        : [],
    }),
  }));
}
